import React, { useState, Suspense, useEffect } from 'react';
import { Canvas, useLoader, useThree } from '@react-three/fiber';
import { OrbitControls, Html, Center, Bounds, ContactShadows, Environment } from '@react-three/drei';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import * as THREE from 'three';
import {
  Container, Title, Text, Button, Grid, createStyles, Paper, Group, Stack,
  Select, Slider, NumberInput, ActionIcon, Tooltip, Alert, Badge
} from '@mantine/core';
import { Dropzone, MIME_TYPES } from '@mantine/dropzone';
import { IconUpload, IconX, Icon3dCubeSphere, IconAlertCircle, IconTrash } from '@tabler/icons-react';
import { useDispatch } from 'react-redux';
import { addCartItem } from '../../redux/cart/cartActions';
import { toast } from 'react-toastify';
import * as api from '../../api';

// --- Styles ---
const useStyles = createStyles((theme) => ({
  wrapper: {
    paddingTop: 80,
    paddingBottom: 80,
    minHeight: '100vh',
    backgroundColor: '#050505',
    backgroundImage: 'radial-gradient(circle at center, #1a1a1a 0%, #000000 100%)',
    overflowX: 'hidden',
  },

  title: {
    color: theme.white,
    fontFamily: `Greycliff CF, ${theme.fontFamily}`,
    fontSize: 44,
    lineHeight: 1.2,
    fontWeight: 900,
    marginBottom: theme.spacing.sm,
    textAlign: 'center',
    [theme.fn.smallerThan('sm')]: {
      fontSize: 32,
    },
  },

  subtitle: {
    color: theme.colors.gray[4],
    textAlign: 'center',
    marginBottom: theme.spacing.xl * 2,
    maxWidth: 700,
    marginLeft: 'auto',
    marginRight: 'auto',
  },

  glassPanel: {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    padding: theme.spacing.xl,
    borderRadius: theme.radius.lg,
    height: '100%',
    color: theme.white,
  },

  viewerContainer: {
    height: 500,
    width: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.02)', // Slightly lighter for the canvas area
    borderRadius: theme.radius.md,
    overflow: 'hidden',
    position: 'relative',
    border: '1px solid rgba(255, 255, 255, 0.05)',
  },

  controlsTitle: {
    color: theme.white,
    fontSize: 24,
    fontWeight: 700,
    marginBottom: theme.spacing.md,
  },

  label: {
    color: theme.colors.gray[3],
    fontSize: theme.fontSizes.sm,
    marginBottom: 4,
  },

  dropzone: {
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
    border: '1px dashed rgba(255, 255, 255, 0.2)',
    borderRadius: theme.radius.md,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
    '&[data-accept]': {
      backgroundColor: 'rgba(40, 200, 40, 0.1)',
      border: '1px dashed rgba(40, 200, 40, 0.5)',
    },
    '&[data-reject]': {
      backgroundColor: 'rgba(200, 40, 40, 0.1)',
      border: '1px dashed rgba(200, 40, 40, 0.5)',
    },
  },

  dropzoneText: {
    color: theme.colors.gray[4],
  },
}));

// --- 3D Viewer Components ---
const Loader = () => (
  <Html center>
    <div style={{ color: 'white', fontWeight: 600 }}>Loading Model...</div>
  </Html>
);

const Model = ({ url, modelColor, onModelLoad }) => {
  const geometry = useLoader(STLLoader, url);
  const threeColor = new THREE.Color(modelColor.toLowerCase());
  const { gl } = useThree();

  useEffect(() => {
    if (gl && geometry) {
      const timeout = setTimeout(() => {
        const dataUrl = gl.domElement.toDataURL('image/png');
        onModelLoad(dataUrl);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [gl, geometry, onModelLoad]);

  return <mesh geometry={geometry} material={new THREE.MeshStandardMaterial({ color: threeColor })} />;
};

// --- Main Component ---
const ModelUploader = () => {
  const { classes, theme } = useStyles();
  const dispatch = useDispatch();

  // State
  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Customization State
  const [material, setMaterial] = useState('PLA');
  const [color, setColor] = useState('Black');
  const [infill, setInfill] = useState(20);
  const [layerHeight, setLayerHeight] = useState('0.2mm');
  const [quantity, setQuantity] = useState(1);

  // Options
  const materials = ['PLA', 'ABS', 'PETG', 'Resin'];
  const colors = ['White', 'Black', 'Grey', 'Red', 'Blue', 'Green'];
  const layerHeights = ['0.1mm', '0.2mm', '0.3mm'];

  const handleDrop = (files) => {
    const selectedFile = files[0];
    if (selectedFile) {
      if (fileUrl) URL.revokeObjectURL(fileUrl);
      setFile(selectedFile);
      setFileUrl(URL.createObjectURL(selectedFile));
      setThumbnailUrl(null);
      toast.info(`Loaded: ${selectedFile.name}`);
    }
  };

  const handleRemoveFile = () => {
    if (fileUrl) URL.revokeObjectURL(fileUrl);
    setFile(null);
    setFileUrl(null);
    setThumbnailUrl(null);
  };

  const handleModelLoad = (dataUrl) => {
    setThumbnailUrl(dataUrl);
  };

  const handleAddToCart = async () => {
    if (!file || !fileUrl || !thumbnailUrl) {
      toast.error("Please upload and view a model first!");
      return;
    }

    setIsUploading(true);
    try {
      const response = await api.uploadModelFile(file);
      const permanentFileUrl = response.data.filePath;

      const customProduct = {
        isCustom: true,
        id: `custom_${Date.now()}`,
        title: file.name,
        thumbnailUrl: thumbnailUrl,
        price: 100, // Placeholder price
        quantity: quantity,
        parameters: { material, color, infill, layerHeight },
        modelFileUrl: permanentFileUrl,
      };

      dispatch(addCartItem(customProduct));
      toast.success("Added to cart successfully!");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload model.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={classes.wrapper}>
      <Container size="xl">
        <Title className={classes.title}>Upload & Customize</Title>
        <Text className={classes.subtitle}>
          Bring your ideas to life. Upload your STL file, customize specs, and order readily.
        </Text>

        <Grid gutter="xl">
          {/* Left Column: 3D Viewer */}
          <Grid.Col md={7}>
            <Paper className={classes.glassPanel}>
              <Group position="apart" mb="md">
                <Title order={3} color="white">3D Viewer</Title>
                {file && (
                  <Button
                    color="red"
                    variant="subtle"
                    size="xs"
                    compact
                    leftIcon={<IconTrash size={16} />}
                    onClick={handleRemoveFile}
                  >
                    Remove Model
                  </Button>
                )}
              </Group>

              <div className={classes.viewerContainer}>
                {fileUrl ? (
                  <Canvas camera={{ position: [0, 0, 50], fov: 50 }} gl={{ antialias: true, preserveDrawingBuffer: true }} dpr={[1, 2]}>
                    <color attach="background" args={['#1a1a1a']} />
                    <ambientLight intensity={0.6} />
                    <directionalLight position={[10, 10, 10]} intensity={1} />
                    <directionalLight position={[-10, -10, -10]} intensity={0.5} />
                    <OrbitControls makeDefault />
                    <Suspense fallback={<Loader />}>
                      <Bounds fit clip observe margin={1.2}>
                        <Center>
                          <Model url={fileUrl} modelColor={color} onModelLoad={handleModelLoad} />
                        </Center>
                      </Bounds>
                      <ContactShadows position={[0, -0.5, 0]} opacity={0.6} scale={10} blur={2.5} far={1} />
                      <Environment preset="studio" />
                    </Suspense>
                  </Canvas>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
                    <Dropzone
                      onDrop={handleDrop}
                      onReject={(files) => toast.error('File rejected. Please upload a valid STL.')}
                      maxSize={50 * 1024 * 1024} // 50MB
                      accept={[MIME_TYPES.stl, 'application/vnd.ms-pki.stl', '.stl']}
                      className={classes.dropzone}
                      style={{ width: '80%', height: '80%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    >
                      <Group position="center" spacing="xl" style={{ minHeight: 220, pointerEvents: 'none' }}>
                        <Dropzone.Accept>
                          <IconUpload size="3.2rem" stroke={1.5} color={theme.colors.cyan[6]} />
                        </Dropzone.Accept>
                        <Dropzone.Reject>
                          <IconX size="3.2rem" stroke={1.5} color={theme.colors.red[6]} />
                        </Dropzone.Reject>
                        <Dropzone.Idle>
                          <Icon3dCubeSphere size="3.2rem" stroke={1.5} color={theme.colors.gray[4]} />
                        </Dropzone.Idle>

                        <div>
                          <Text size="xl" inline className={classes.dropzoneText}>
                            Drag & Drop STL file here
                          </Text>
                          <Text size="sm" color="dimmed" inline mt={7}>
                            or click to select file
                          </Text>
                        </div>
                      </Group>
                    </Dropzone>
                  </div>
                )}
              </div>

              {/* Upload Note */}
              <Alert icon={<IconAlertCircle size="1rem" />} title="Important Note" color="blue" variant="filled" mt="md" radius="md" styles={{
                root: { backgroundColor: 'rgba(34, 139, 230, 0.15)', border: '1px solid rgba(34, 139, 230, 0.3)' },
                title: { color: theme.white },
                message: { color: theme.colors.gray[3] }
              }}>
                Please upload separate STL files for different parts if they need unique specifications (color, material, etc.).
              </Alert>

            </Paper>
          </Grid.Col>

          {/* Right Column: Customization */}
          <Grid.Col md={5}>
            <Paper className={classes.glassPanel}>
              <Title className={classes.controlsTitle}>Print Settings</Title>

              <Stack spacing="lg">
                <Grid>
                  <Grid.Col span={6}>
                    <Text className={classes.label}>Material</Text>
                    <Select
                      data={materials}
                      value={material}
                      onChange={setMaterial}
                      variant="filled"
                      styles={{ input: { backgroundColor: 'rgba(255, 255, 255, 0.05)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.1)' } }}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text className={classes.label}>Color</Text>
                    <Select
                      data={colors}
                      value={color}
                      onChange={setColor}
                      variant="filled"
                      styles={{ input: { backgroundColor: 'rgba(255, 255, 255, 0.05)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.1)' } }}
                    />
                  </Grid.Col>
                </Grid>

                <div>
                  <Text className={classes.label}>Infill Density: {infill}%</Text>
                  <Slider
                    value={infill}
                    onChange={setInfill}
                    min={0}
                    max={100}
                    step={5}
                    color="cyan"
                    marks={[
                      { value: 20, label: '20%' },
                      { value: 50, label: '50%' },
                      { value: 100, label: 'Solid' },
                    ]}
                    mb="xl"
                  />
                </div>

                <Grid>
                  <Grid.Col span={6}>
                    <Text className={classes.label}>Layer Height</Text>
                    <Select
                      data={layerHeights}
                      value={layerHeight}
                      onChange={setLayerHeight}
                      variant="filled"
                      styles={{ input: { backgroundColor: 'rgba(255, 255, 255, 0.05)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.1)' } }}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Text className={classes.label}>Quantity</Text>
                    <NumberInput
                      value={quantity}
                      onChange={setQuantity}
                      min={1}
                      variant="filled"
                      styles={{ input: { backgroundColor: 'rgba(255, 255, 255, 0.05)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.1)' } }}
                    />
                  </Grid.Col>
                </Grid>

                <Button
                  fullWidth
                  size="lg"
                  variant="gradient"
                  gradient={{ from: 'cyan', to: 'indigo' }}
                  onClick={handleAddToCart}
                  loading={isUploading}
                  disabled={!file}
                  mt="md"
                >
                  {isUploading ? 'Processing...' : 'Add to Cart'}
                </Button>
              </Stack>
            </Paper>
          </Grid.Col>
        </Grid>
      </Container>
    </div>
  );
};

export default ModelUploader;
